import {useQuery} from '@tanstack/react-query'
import moment from 'moment-timezone'

import {useQuantity} from '../../../fields/quantity/useQuantity'
import {
  AvailableTimeSlotsResult,
  BuildSlotRequestResult,
  processAvailabilityResponse,
  UseAvailableTimeSlotsParams,
  useSlotManagementContext,
  useSlotRequest
} from '../../../slotManagement'
import {calculateAutoConfirmAvailability} from '../../../slotManagement/availability/calculateAutoConfirmAvailability'
import {calculateSelectedSlotValidation} from '../../../slotManagement/availability/calculateSelectedSlotValidation'

import {useOrderIntakeSlotOrgUnitId} from './useOrderIntakeSlotOrgUnitId'

/**
 * Internal hook for Order Intake slot availability.
 * Simpler than Order Management - no auto-confirmable logic.
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber).
 *
 * Query is disabled when:
 * - Feature toggle is off
 * - Quantity exceeds max auto-confirmable
 * - Slot request params cannot be built (validation failed)
 * - No query function provided
 */
export const useOrderIntakeSlotAvailability = ({
  dateIndex = 0,
  slotAvailabilityQueryFn,
  selectedDateTime,
  timeZone
}: UseAvailableTimeSlotsParams & {
  selectedDateTime?: string
  timeZone?: string
}): AvailableTimeSlotsResult => {
  const {config} = useSlotManagementContext()
  const orgUnitId = useOrderIntakeSlotOrgUnitId(config.orgUnitId)
  const quantityReturn = useQuantity()
  const isMaxAutoConfirmQuantityExceeded =
    quantityReturn.type === 'volume' ? quantityReturn.isMaxAutoConfirmQuantityExceeded : false

  // Build slot request parameters
  const slotRequestResult: BuildSlotRequestResult = useSlotRequest({
    orgUnitId,
    siteNumber: config.siteNumber,
    dateIndex,
    normalizeDate: true, // Availability endpoint needs normalized date (12:00 PM UTC)
    // V3 availability is keyed by the site-local day, not the timestamp's UTC day.
    resolveDateTime: timeZone
      ? (value) =>
          value ? `${moment(value).tz(timeZone).format('YYYY-MM-DD')}T12:00:00.000Z` : value
      : undefined
  })

  // Determine if we should call API
  const shouldQueryApi =
    config.isFeatureEnabled &&
    !isMaxAutoConfirmQuantityExceeded &&
    slotRequestResult.isValid &&
    !!slotAvailabilityQueryFn

  // Get query options and execute query
  const queryOptions =
    shouldQueryApi && slotRequestResult.params
      ? slotAvailabilityQueryFn(slotRequestResult.params)
      : null

  const queryInfo = useQuery(
    queryOptions ?? {
      queryKey: ['slot-availability-disabled'],
      queryFn: () => ({availableSlots: []}),
      enabled: false
    }
  )

  // Process API response
  const processedResponse = queryInfo.isSuccess
    ? processAvailabilityResponse(queryInfo.data)
    : {availableSlots: [], isOutOfAutoConfirmLeadTime: false}

  // Return available slots from API response (or empty if disabled)
  // Map API slots (ISO format) to HH:mm format
  const formatSlot = (slot: string) =>
    (timeZone ? moment(slot).tz(timeZone) : moment(slot)).format('HH:mm')
  const availableSlots = processedResponse.availableSlots.map(formatSlot)

  // Calculate auto-confirm availability status
  const autoConfirmAvailability = calculateAutoConfirmAvailability({
    queryError: queryInfo.isError,
    isQuantityExceeded: isMaxAutoConfirmQuantityExceeded,
    apiIsOutOfLeadTime: processedResponse.isOutOfAutoConfirmLeadTime,
    isManualSlots: false // Order Intake never uses manual slots
  })

  // Get currently selected time in HH:mm format
  const selectedTime = selectedDateTime ? formatSlot(selectedDateTime) : ''

  // Calculate selected slot validation
  const selectedSlot = calculateSelectedSlotValidation({
    selectedDateTime: selectedTime,
    availableSlots,
    autoConfirmUnavailable: autoConfirmAvailability.isUnavailable
  })

  // Warn if params couldn't be built but feature is enabled
  if (config.isFeatureEnabled && !slotRequestResult.isValid) {
    console.warn(
      '[useOrderIntakeSlotAvailability] Could not build slot request params. Missing fields:',
      slotRequestResult.missingFields
    )
  }

  return {
    availableSlots,
    autoConfirmAvailability,
    selectedSlot,
    queryInfo
  }
}
